import React, { useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { APP_ROUTES, ROUTE_PATHS } from "../../../../constants/app.constants";
import NavigationTabs from "../../../distributor-settings/network-access/navigation-tabs";
import CompanySettings from "../../../distributor-settings/company-settings";
import NetworkAccessPage from "../../../distributor-settings/network-access";
import MembersAccessPage from "../../../distributor-settings/members-access";
import TagsPage from "../../../distributor-settings/tags";
import SubscriptionsPage from "../../../distributor-settings/subscriptions";
import EmailTemplatesPage from "../../../distributor-settings/email-templates";
import StatusesPage from "../../../hg-settings/statuses";
import { DistributorSettingsSideMenu } from "./side-menu/side-menu";
import styles from "./distributor-settings.module.scss";

const DistributorSettings = ({ isDistributorView = false }) => {
  const { distributorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const basePath = ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.settings(distributorId);

  const getRoutePath = useCallback((routeKey) => {
    // When in distributor view, use relative paths
    if (isDistributorView) {
      return routeKey;
    }
    const routeMap = {
      'company-settings': APP_ROUTES.subscriptionPortalPages.distributorSettingsCompanySettings,
      'members-access': APP_ROUTES.subscriptionPortalPages.distributorSettingsMembersAccess,
      'tags': APP_ROUTES.subscriptionPortalPages.distributorSettingsTags,
      'statuses': APP_ROUTES.subscriptionPortalPages.distributorSettingsStatuses,
      'network-access': APP_ROUTES.subscriptionPortalPages.distributorSettingsNetworkAccess,
      'email-templates': APP_ROUTES.subscriptionPortalPages.distributorSettingsEmailTemplates,
      'subscriptions': APP_ROUTES.subscriptionPortalPages.distributorSettingsSubscriptions,
    };
    // Return relative path for Route components, but full path for navigation
    return routeMap[routeKey] || routeKey;
  }, [isDistributorView]);
  
  const getFullRoutePath = useCallback((routeKey) => {
    // When in distributor view, use basePath + relative path
    if (isDistributorView) {
      return `${basePath}/${routeKey}`;
    }
    const routeMap = {
      'company-settings': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsCompanySettings,
      'members-access': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsMembersAccess,
      'tags': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsTags,
      'statuses': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsStatuses,
      'network-access': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsNetworkAccess,
      'email-templates': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsEmailTemplates,
      'subscriptions': ROUTE_PATHS.subscriptionPortalPages.distributorSettingsSubscriptions,
    };
    return routeMap[routeKey] || `${basePath}/${routeKey}`;
  }, [basePath, isDistributorView]);

  const navigationTabs = useMemo(() => [
    { 
      label: "Company Settings", 
      active: currentPath === getFullRoutePath('company-settings') || currentPath === basePath, 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('company-settings'))
    },
    { 
      label: "Members and Access", 
      active: currentPath === getFullRoutePath('members-access'), 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('members-access'))
    },
    { 
      label: "Tags", 
      active: currentPath === getFullRoutePath('tags'), 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('tags'))
    },
    { 
      label: "Statuses", 
      active: currentPath === getFullRoutePath('statuses'), 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('statuses'))
    },
    { 
      label: "Network Access", 
      active: currentPath === getFullRoutePath('network-access'), 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('network-access'))
    },
    { 
      label: "Email Templates", 
      active: currentPath === getFullRoutePath('email-templates'), 
      disabled: false, 
      onClick: () => navigate(getFullRoutePath('email-templates'))
    },
  ], [currentPath, basePath, navigate, getFullRoutePath]);

  return (
    <div className={styles.container}>
      {isDistributorView && (
        <div className={styles.sideMenu}>
          <DistributorSettingsSideMenu basePath={basePath} getRoutePath={getFullRoutePath} />
        </div>
      )}
      <div className={styles.mainContent}>
        {!isDistributorView && (
          <div className={styles.navContainer}>
            <NavigationTabs tabs={navigationTabs} />
          </div>
        )}
        <div className={styles.content}>
          <Routes>
            <Route
              path={getRoutePath('company-settings')}
              element={<CompanySettings isDistributorView={isDistributorView} />}
            />
            <Route
              path={getRoutePath('network-access')}
              element={<NetworkAccessPage />}
            />
            <Route
              path={getRoutePath('members-access')}
              element={<MembersAccessPage />}
            />
            <Route
              path={getRoutePath('subscriptions')}
              element={<SubscriptionsPage />}
            />
            <Route
              path={getRoutePath('tags')}
              element={<TagsPage />}
            />
            <Route
              path={getRoutePath('statuses')}
              element={<StatusesPage />}
            />
            <Route
              path={getRoutePath('email-templates')}
              element={<EmailTemplatesPage />}
            />
            <Route 
              path="" 
              element={
                <Navigate 
                  to={isDistributorView ? getRoutePath('company-settings') : getFullRoutePath('company-settings')} 
                  replace 
                />
              } 
            />
            <Route 
              path="*" 
              element={
                <Navigate 
                  to={isDistributorView ? getRoutePath('company-settings') : getFullRoutePath('company-settings')} 
                  replace 
                />
              } 
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default DistributorSettings;

